import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Users, Briefcase, Home, Wallet, Globe, Smartphone, ArrowRight, Star } from 'lucide-react';
import AuraLogo from '../components/AuraLogo';

const FEATURES = [
  {
    icon: BookOpen,
    title: 'Study Hub',
    desc: 'Notes, flashcards, study materials, attendance & CGPA tracker',
    color: '#1a73e8',
    lightBg: '#EFF6FF',
  },
  {
    icon: Users,
    title: 'Student Social',
    desc: 'Campus feed, forums, knowledge loop, and peer connections',
    color: '#188038',
    lightBg: '#ECFDF5',
  },
  {
    icon: Briefcase,
    title: 'Career Tools',
    desc: 'Resume builder, LinkedIn optimizer, opportunities & scholarships',
    color: '#1257a0',
    lightBg: '#EFF6FF',
  },
  {
    icon: Home,
    title: 'Campus Life',
    desc: 'Food & cab sharing, PG finder, marketplace, sports & hackathons',
    color: '#b06000',
    lightBg: '#FFFBEB',
  },
  {
    icon: Wallet,
    title: 'Money & Docs',
    desc: 'Expense groups, document vault, and academic grievances',
    color: '#188038',
    lightBg: '#ECFDF5',
  },
  {
    icon: Globe,
    title: 'College Network',
    desc: 'Study groups, project collaborations, and campus initiatives',
    color: '#1a73e8',
    lightBg: '#EFF6FF',
  },
];

export default function MarketingPage() {
  return (
    <div style={{ fontFamily: 'Inter,system-ui,-apple-system,sans-serif', background: '#fff', minHeight: '100vh', color: '#202124' }}>

      {/* ── Sticky nav ── */}
      <nav style={{
        position: 'sticky', top: 0, zIndex: 50,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(8px)',
        borderBottom: '1px solid #f1f3f4',
        padding: '0 24px',
        height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
          <AuraLogo size={32} />
          <span style={{ fontWeight: 800, fontSize: 20, color: '#1a73e8', letterSpacing: '-0.5px' }}>Aura</span>
        </Link>
        <Link
          to="/login"
          style={{
            background: '#1a73e8', color: '#fff',
            padding: '8px 20px', borderRadius: 10,
            fontWeight: 600, fontSize: 14,
            textDecoration: 'none',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#1765cc'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#1a73e8'; }}
        >
          Sign In
        </Link>
      </nav>

      {/* ── Hero ── */}
      <section style={{
        background: 'linear-gradient(135deg, #1257a0 0%, #1a73e8 60%, #4a9ff5 100%)',
        padding: '80px 24px 72px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(255,255,255,0.15)',
            borderRadius: 999, padding: '6px 16px',
            fontSize: 13, color: '#fff', fontWeight: 500,
            marginBottom: 12,
          }}>
            <Star size={13} fill="currentColor" />
            Built for Indian college students
          </div>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.20)',
            borderRadius: 999, padding: '4px 12px',
            fontSize: 12, color: 'rgba(255,255,255,0.75)', fontWeight: 500,
            marginBottom: 20,
          }}>
            A{' '}
            <a href="https://quantaiseed.com" target="_blank" rel="noopener noreferrer"
              style={{ color: '#fff', fontWeight: 700, textDecoration: 'none' }}>
              QuantAISeed
            </a>
            {' '}product
          </div>
          <h1 style={{
            fontSize: 'clamp(36px, 8vw, 64px)',
            fontWeight: 900, color: '#fff',
            lineHeight: 1.1, letterSpacing: '-1.5px',
            margin: '0 0 20px',
          }}>
            Your Campus,<br />Simplified.
          </h1>
          <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.80)', lineHeight: 1.6, margin: '0 0 36px' }}>
            One app for everything college — study tools, campus social,
            career prep, housing, and more.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link
              to="/register"
              style={{
                background: '#fff', color: '#1a73e8',
                padding: '14px 28px', borderRadius: 12,
                fontWeight: 700, fontSize: 15,
                textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
              }}
            >
              Get Started Free <ArrowRight size={16} />
            </Link>
            <Link
              to="/login"
              style={{
                background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1.5px solid rgba(255,255,255,0.35)',
                padding: '14px 28px', borderRadius: 12,
                fontWeight: 600, fontSize: 15,
                textDecoration: 'none',
              }}
            >
              Sign In
            </Link>
          </div>
          <p style={{ marginTop: 24, fontSize: 13, color: 'rgba(255,255,255,0.55)' }}>
            Free to join · No credit card required
          </p>
        </div>
      </section>

      {/* ── Features grid ── */}
      <section style={{ maxWidth: 1040, margin: '0 auto', padding: '72px 24px 56px' }}>
        <h2 style={{
          textAlign: 'center', fontSize: 28, fontWeight: 800,
          color: '#202124', letterSpacing: '-0.5px', marginBottom: 8,
        }}>
          Everything you need at college
        </h2>
        <p style={{ textAlign: 'center', color: '#5f6368', fontSize: 16, marginBottom: 48, marginTop: 0 }}>
          Six powerful zones — one seamless experience.
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 20,
        }}>
          {FEATURES.map(f => {
            const Icon = f.icon;
            return (
              <Link
                key={f.title}
                to="/login"
                style={{ textDecoration: 'none' }}
              >
                <div
                  style={{
                    background: '#fff',
                    border: '1px solid #e8eaed',
                    borderRadius: 16,
                    padding: '24px 22px',
                    height: '100%',
                    transition: 'transform 0.15s, box-shadow 0.15s',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-3px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.10)';
                    e.currentTarget.style.borderColor = f.color + '55';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '';
                    e.currentTarget.style.borderColor = '#e8eaed';
                  }}
                >
                  <div style={{
                    width: 44, height: 44, borderRadius: 12,
                    background: f.lightBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: 16,
                  }}>
                    <Icon size={22} color={f.color} />
                  </div>
                  <h3 style={{ fontWeight: 700, fontSize: 16, color: '#202124', margin: '0 0 6px' }}>{f.title}</h3>
                  <p style={{ fontSize: 14, color: '#5f6368', lineHeight: 1.55, margin: '0 0 16px' }}>{f.desc}</p>
                  <span style={{
                    fontSize: 13, fontWeight: 600, color: f.color,
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                  }}>
                    Explore <ArrowRight size={12} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── PWA install tip ── */}
      <section style={{ maxWidth: 1040, margin: '0 auto', padding: '0 24px 72px' }}>
        <div style={{
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: 16,
          padding: '24px 28px',
          display: 'flex', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap',
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: '#1a73e8',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Smartphone size={22} color="#fff" />
          </div>
          <div>
            <p style={{ fontWeight: 700, fontSize: 15, color: '#1257a0', margin: '0 0 4px' }}>
              Install Aura on Android
            </p>
            <p style={{ fontSize: 14, color: '#1a73e8', margin: 0, lineHeight: 1.5 }}>
              Open in Chrome → tap the menu (three dots) → <strong>Add to Home Screen</strong>.
              Aura works offline and opens like a native app — no Play Store needed.
            </p>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer style={{
        borderTop: '1px solid #f1f3f4',
        padding: '28px 24px',
        textAlign: 'center',
        color: '#9aa0a6',
        fontSize: 13,
      }}>
        © 2025 Aura ·{' '}
        <a href="https://quantaiseed.com" target="_blank" rel="noopener noreferrer"
          style={{ color: '#9aa0a6', textDecoration: 'none' }}>
          A QuantAISeed product
        </a>
        {' · '}
        <Link to="/login" style={{ color: '#9aa0a6', textDecoration: 'none' }}>Sign In</Link>
        {' · '}
        <Link to="/register" style={{ color: '#9aa0a6', textDecoration: 'none' }}>Register</Link>
      </footer>
    </div>
  );
}
